/* ============================================================
   AuthContext — global session state (login/register/logout).

   Tokens live in the tokenStore (localStorage); this context
   mirrors them and stays in sync even when the API client
   silently refreshes or drops the session after a 401.
   ============================================================ */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import { clearAuth, loadAuth, saveAuth, subscribe } from './tokenStore';
import type { AuthResponse, LoginRequest, RegisterRequest } from '../types/auth';

interface AuthContextValue {
  /** Current session (tokens + profile) or null when anonymous. */
  auth: AuthResponse | null;
  isAuthenticated: boolean;
  /** Throws Error with the API message on failure. */
  login: (dto: LoginRequest) => Promise<void>;
  /** Throws Error with the API message on failure. */
  register: (dto: RegisterRequest) => Promise<void>;
  /** Drops the local session and revokes the refresh token (best-effort). */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthResponse | null>(() => loadAuth());

  // Keep context in sync with store mutations from anywhere (e.g. 401 refresh).
  useEffect(() => subscribe(setAuth), []);

  const login = useCallback(async (dto: LoginRequest) => {
    const session = await api.login(dto);
    saveAuth(session);
  }, []);

  const register = useCallback(async (dto: RegisterRequest) => {
    const session = await api.register(dto);
    saveAuth(session);
  }, []);

  const logout = useCallback(async () => {
    const current = loadAuth();
    clearAuth(); // local session first, so the UI updates immediately
    if (current) {
      await api.logout(current.accessToken, current.refreshToken);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      auth,
      isAuthenticated: auth !== null,
      login,
      register,
      logout,
    }),
    [auth, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
